// Only trigger in SCORM tracking
import { completedSeqFlowPostfix } from '../../../../features/tracking/models/CompletedItemsStore';
import { Guid } from '../../../model/guid';
import { ItemsReducerState } from '../../../reducers/items/itemsReducer';
import { GetItemById } from '../../../reducers/items/itemsSelectors';
import { TrackingDataVersion } from '../clients/trackingClient';
import {
  createGetItemByIdFromItemsReducerState,
  createGetMaybeItemByIdFromItemsReducerState,
} from './getItemByIdFromItemsReducerState';
import { Hotspot, isNonSequencedFlowItem, isSequencedFlowItem, isSequencedFlowStep } from '@ael/core-api';
import { List, Set } from 'immutable';

export const getChildrenForScormCreator =
  (getItems: () => ItemsReducerState) =>
  (itemId: Guid, finishedItemIds?: Set<string>, trackingDataVersion?: TrackingDataVersion): List<Guid> | null => {
    const items = getItems();
    const getItemById = createGetItemByIdFromItemsReducerState(items);
    const getItemByIdAllowUndefined = createGetMaybeItemByIdFromItemsReducerState(items);
    const item = getItemByIdAllowUndefined(itemId);
    if (!item) {
      return null;
    }
    if (isNonSequencedFlowItem(item)) {
      const tabIds = item.hotspots
        .map((hotspot: Guid) => getHotspotsTabIds(hotspot, getItemById))
        .toList()
        .flatten();
      if (!item.startingMessageId) {
        return tabIds.toList() as List<Guid>;
      }

      return tabIds.concat(item.startingMessageId).toList() as List<Guid>;
    }
    // LEGACY: After removing presenter, we have to check if it was completed and migrate the data to completed sequenced content.
    if (trackingDataVersion === TrackingDataVersion.v1 && isSequencedFlowItem(item) && finishedItemIds) {
      const children = item.contentItems;
      const containsAnyChildren =
        finishedItemIds.filter((finishedItemId) => children.some((childId) => childId === finishedItemId)).count() > 0;
      if (!containsAnyChildren) {
        return item.contentItems.concat(item.id + completedSeqFlowPostfix).toList();
      }
    }
    // LEGACY: Kind of same as below, but we only need to check if Presenter was started, as "completion" scenario is handled above.
    // Ugly way of doing it, but hopefully we can remove this piece of code in two years.
    if (trackingDataVersion === TrackingDataVersion.v1 && isSequencedFlowStep(item) && finishedItemIds) {
      if (!item.parentId.parentId) {
        return null;
      }
      // We know that if it is already there, it was not a presenter. Do nothing
      if (finishedItemIds.contains(item.parentId.parentId)) {
        return null;
      }
      return List([item.id, item.parentId.parentId]);
    }
    return null;
  };

const getHotspotsTabIds = (hotspotId: Guid, getItemById: GetItemById): List<Guid> => {
  return (getItemById(hotspotId) as Hotspot).tabs;
};
